import { useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { BORNES_BULLE, COULEURS, type Couleur } from '@nextilt/commun';
import { api } from '../partage/api/client';
import { cles } from '../partage/relectures';
import { useAjouterParLePlus, useCreerUnProjet, useGarderParLePlus } from './lePlus';
import { useDossiers, useFeuille } from '../partage/useArbre';
import { ImporterPlan } from '../import-ia';
import { Modele } from '../modeles';
import {
  champsDeLaSorte,
  couleurProposee,
  feuilleDuPlus,
  jourDeLaJournee,
  jourDuPlus,
  peutAjouter,
  projetDuPlus,
  racineDeLaFeuille,
  sortesDuPlus,
  type PageDuPlus,
  type SorteDuPlus,
} from './lePlusSelonLaPage';
import { enMots } from '../maintenant';
import { aujourdhui as leJourDAujourdhui, dureeLisible } from '../partage/datesEnMots';
import { garderLeDernierProjet, lireLeDernierProjet } from './dernierProjet';
import { useEnLigne } from './enLigne';
import { FeuilleDuBas } from '../partage/FeuilleDuBas';
import { CREER, EXEMPLE_DE_PROJET, MOTS_DU_PLUS as MOTS, NOM_DU_NOUVEAU_PROJET, NOUVEAU_PROJET } from '../partage/vocabulaire';
import { teinteDuProjet, teinteOuAccent } from '../apparence';
import './Capture.css';

/**
 * Le + du téléphone : il ajoute là où l'on est (chantier `telephone-projets-fiche`, ticket 05,
 * décisions d'Igor du 2026-10-05, revue 20 et 21, cadrage 9, 14 et 17 ; maquette
 * `design/telephone.html`, « Le + dans l'Agenda » et « Nouveau projet »). Les règles sont dans
 * `lePlusSelonLaPage.ts`.
 *
 * - Tous les projets : un nouveau projet, son nom et sa couleur parmi les cinq ; dessous, partir
 *   d'un modèle ou importer un plan.
 * - Un projet ou une étape : une Tâche ou une Idée, dedans.
 * - Journée : une tâche de plus dans la journée d'aujourd'hui, nouvelle ou parmi les possibles.
 * - L'Agenda : une Tâche (elle entre dans la journée du jour), un Rendez-vous (une heure) ou une
 *   Échéance (à faire avant ce jour), dans le projet choisi, à sa racine.
 * - Maintenant : une tâche dans un projet choisi, sans plus (en attendant la décision 17).
 *
 * **Il n'y a jamais de page blanche** et un mot suffit : le titre est le seul champ, plus l'heure
 * d'un rendez-vous. Hors ligne, Tâche et Idée partent dans la file (docs/adr/0001), le reste dit
 * qu'il faut le réseau. La feuille remonte du bas et laisse voir ce qu'il y a dessous.
 */
export function Capture({ page, onFermer }: { page: PageDuPlus; onFermer: () => void }) {
  const dossiers = useDossiers();
  const aDesProjets = !dossiers.data || dossiers.data.dossiers.length > 0;
  return feuilleDuPlus(page, aDesProjets) === 'nouveau-projet' ? (
    <NouveauProjetDuPlus sansProjet={page.sorte !== 'projets'} onFermer={onFermer} />
  ) : (
    <AjoutDuPlus page={page} onFermer={onFermer} />
  );
}

/**
 * La feuille du bas du + (`FeuilleDuBas`) : poignée, titre, ×, sous un voile qui la ferme ;
 * Échap aussi. Par-dessus tout le reste, et plus haute que les autres feuilles.
 */
function FeuilleDuPlus({
  titre,
  quoi,
  onFermer,
  children,
}: {
  titre: string;
  quoi: 'ajout' | 'nouveau-projet';
  onFermer: () => void;
  children: ReactNode;
}) {
  return (
    <FeuilleDuBas titre={titre} onFermer={onFermer} croix={MOTS.fermer} dessus classe="capture" attributs={{ 'data-plus': quoi }}>
      {children}
    </FeuilleDuBas>
  );
}

/**
 * Un nouveau projet : son nom, sa couleur (la moins portée est proposée), « Créer », qui ouvre le
 * projet ; dessous, à part, « Partir d'un modèle › » et « Importer un plan écrit par une IA › »
 * (`Modele`, `ImporterPlan`, ce qui existe aujourd'hui).
 */
function NouveauProjetDuPlus({ sansProjet, onFermer }: { sansProjet: boolean; onFermer: () => void }) {
  const naviguer = useNavigate();
  const dossiers = useDossiers();
  const creer = useCreerUnProjet();
  const [nom, setNom] = useState('');
  const [choisie, setChoisie] = useState<Couleur | null>(null);
  const [ailleurs, setAilleurs] = useState<'modele' | 'import' | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const couleur = choisie ?? couleurProposee((dossiers.data?.dossiers ?? []).map((d) => d.couleur));

  if (ailleurs === 'modele') return <Modele onFermer={onFermer} />;
  if (ailleurs === 'import') return <ImporterPlan onFermer={onFermer} />;

  function envoyer(e: FormEvent) {
    e.preventDefault();
    const titre = nom.trim();
    if (!titre || creer.isPending) return;
    setErreur(null);
    creer.mutate(
      { titre, couleur },
      {
        onSuccess: (id) => {
          onFermer();
          if (!id) return;
          garderLeDernierProjet(id);
          naviguer(`/feuille/${id}`);
        },
        onError: (e) => setErreur(enMots(e)),
      }
    );
  }

  return (
    <FeuilleDuPlus titre={NOUVEAU_PROJET} quoi="nouveau-projet" onFermer={onFermer}>
      {sansProjet && <p className="capture-note">{MOTS.dAbordUnProjet}</p>}
      <form className="capture-formulaire" onSubmit={envoyer}>
        <input
          className="capture-champ"
          value={nom}
          placeholder={EXEMPLE_DE_PROJET}
          aria-label={NOM_DU_NOUVEAU_PROJET}
          maxLength={BORNES_BULLE.titreMax}
          autoFocus
          onChange={(e) => setNom(e.target.value)}
        />
        <div className="capture-couleurs">
          <span className="capture-etiquette">{MOTS.saCouleur}</span>
          <div className="capture-pastilles" role="radiogroup" aria-label={MOTS.saCouleur}>
            {COULEURS.map((c) => (
              <button
                key={c}
                type="button"
                role="radio"
                className="capture-couleur"
                data-couleur={c}
                aria-checked={c === couleur}
                aria-label={MOTS.couleurs[c]}
                style={{ ['--teinte' as string]: teinteOuAccent(c) }}
                onClick={() => setChoisie(c)}
              >
                <span aria-hidden="true" />
              </button>
            ))}
          </div>
        </div>
        {erreur && (
          <p className="capture-message" role="status">
            {erreur}
          </p>
        )}
        <button type="submit" className="bouton capture-ajouter" disabled={!nom.trim() || creer.isPending}>
          {CREER}
        </button>
      </form>
      <div className="capture-chemins">
        <button type="button" className="capture-chemin" onClick={() => setAilleurs('modele')}>
          {MOTS.partirDUnModele}
        </button>
        <button type="button" className="capture-chemin" onClick={() => setAilleurs('import')}>
          {MOTS.importerUnPlan}
        </button>
      </div>
    </FeuilleDuPlus>
  );
}

/** Ajouter là où l'on est : ce que c'est, le titre, et seulement les champs utiles. */
function AjoutDuPlus({ page, onFermer }: { page: PageDuPlus; onFermer: () => void }) {
  const enLigne = useEnLigne();
  const dossiers = useDossiers();
  const racines = dossiers.data?.dossiers ?? [];
  const feuille = useFeuille(page.sorte === 'feuille' ? page.id : '');
  const ajouter = useAjouterParLePlus();
  const garder = useGarderParLePlus();

  const aujourdhui = leJourDAujourdhui();
  const jour = jourDuPlus(page, aujourdhui);
  const sortes = sortesDuPlus(page, enLigne);
  const [voulue, setVoulue] = useState<SorteDuPlus>(sortes[0]?.sorte ?? 'tache');
  // Le réseau tombe pendant qu'on écrit : on revient à ce qui passe encore.
  const sorte = sortes.find((s) => s.sorte === voulue)?.possible ? voulue : 'tache';

  const [titre, setTitre] = useState('');
  const [heure, setHeure] = useState('');
  const [choisi, setChoisi] = useState<string | null>(null);
  const [choisitLeProjet, setChoisitLeProjet] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const champ = useRef<HTMLInputElement>(null);

  const dansLaFeuille = page.sorte === 'feuille';
  const projet = choisi ?? projetDuPlus(page, lireLeDernierProjet(), racines.map((r) => r.id));
  const leProjet = racines.find((r) => r.id === projet) ?? null;
  const parentId = dansLaFeuille ? page.id : projet;
  const journee = enLigne ? jourDeLaJournee(page, sorte, aujourdhui) : null;
  const titreDeLaFeuille =
    page.sorte === 'feuille'
      ? MOTS.ajouterDans(feuille.data?.bulle.titre ?? '…')
      : page.sorte === 'agenda' && jour
        ? MOTS.ajouterAuJour(jour)
        : page.sorte === 'journee'
          ? MOTS.ajouterATaJournee
          : MOTS.ajouterUneTache;

  // Journée : ce qui est possible aujourd'hui, à garder d'un toucher.
  const possibles = useQuery({
    queryKey: cles.preparation(aujourdhui, 'plus'),
    queryFn: () => api.preparation(aujourdhui),
    enabled: page.sorte === 'journee' && enLigne,
  });

  function envoyer(e: FormEvent) {
    e.preventDefault();
    if (!parentId || !peutAjouter(sorte, titre, heure) || ajouter.isPending) return;
    const propre = titre.trim();
    setMessage(null);
    ajouter.mutate(
      { titre: propre, parentId, champs: champsDeLaSorte(sorte, jour, heure), journee, aujourdhui },
      {
        onSuccess: (fait) => {
          const racine = dansLaFeuille ? (feuille.data ? racineDeLaFeuille(feuille.data) : null) : parentId;
          if (racine) garderLeDernierProjet(racine);
          if (!fait.refusDuJour) return onFermer();
          setTitre('');
          setMessage(MOTS.pasDansLaJournee(propre, fait.refusDuJour));
          champ.current?.focus();
        },
        onError: (erreur) => setMessage(enMots(erreur)),
      }
    );
  }

  function garderLaTache(id: string) {
    setMessage(null);
    garder.mutate(id, { onSuccess: onFermer, onError: (erreur) => setMessage(enMots(erreur)) });
  }

  return (
    <FeuilleDuPlus titre={titreDeLaFeuille} quoi="ajout" onFermer={onFermer}>
      {sortes.length > 1 && (
        <div className="capture-sortes" role="group" aria-label={titreDeLaFeuille}>
          {sortes.map((s) => (
            <button
              key={s.sorte}
              type="button"
              data-sorte={s.sorte}
              aria-pressed={sorte === s.sorte}
              disabled={!s.possible}
              title={s.possible ? undefined : MOTS.ilFautLeReseau}
              onClick={() => setVoulue(s.sorte)}
            >
              {MOTS.sortes[s.sorte]}
            </button>
          ))}
        </div>
      )}

      <form className="capture-formulaire" onSubmit={envoyer}>
        {/* Un mot suffit. Le champ est souligné, pas encadré : on écrit, on ne remplit pas. */}
        <input
          ref={champ}
          className="capture-champ"
          value={titre}
          placeholder={MOTS.champ}
          aria-label={MOTS.champ}
          maxLength={BORNES_BULLE.titreMax}
          autoFocus
          onChange={(e) => setTitre(e.target.value)}
        />

        <div className="capture-puces">
          {dansLaFeuille ? (
            <span className="capture-dans" style={{ ['--teinte' as string]: teinteDuProjet(feuille.data?.couleur) }}>
              <span className="capture-point" aria-hidden="true" />
              {feuille.data?.bulle.titre ?? '…'}
            </span>
          ) : (
            <button
              type="button"
              className="capture-dedans"
              aria-expanded={choisitLeProjet}
              aria-label={`${MOTS.leProjet} : ${leProjet?.titre ?? ''}`}
              style={{ ['--teinte' as string]: teinteDuProjet(leProjet?.couleur) }}
              onClick={() => setChoisitLeProjet(!choisitLeProjet)}
            >
              <span className="capture-point" aria-hidden="true" />
              <span className="capture-dedans-nom">{leProjet?.titre ?? '…'}</span>
              <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <path d="M2.2 4L6 7.8L9.8 4" stroke="currentColor" strokeWidth="1.5" />
              </svg>
            </button>
          )}
          {page.sorte === 'agenda' && jour && sorte === 'tache' && journee && (
            <span className="capture-puce capture-puce--journee">◷ {MOTS.dansTaJourneeDu(jour)}</span>
          )}
          {page.sorte === 'agenda' && jour && sorte === 'echeance' && (
            <span className="capture-puce capture-puce--echeance">⚑ {MOTS.avantLe(jour)}</span>
          )}
          {sorte === 'rendez_vous' && (
            <label className="capture-heure">
              {MOTS.heure}
              <input type="time" value={heure} aria-label={MOTS.heureDuRendezVous} onChange={(e) => setHeure(e.target.value)} />
            </label>
          )}
        </div>

        {choisitLeProjet && !dansLaFeuille && (
          <ul className="capture-choix">
            {racines.map((d) => (
              <li key={d.id}>
                <button
                  type="button"
                  aria-pressed={d.id === projet}
                  style={{ ['--teinte' as string]: teinteDuProjet(d.couleur) }}
                  onClick={() => {
                    setChoisi(d.id);
                    setChoisitLeProjet(false);
                  }}
                >
                  <span className="capture-point" aria-hidden="true" />
                  {d.titre}
                </button>
              </li>
            ))}
          </ul>
        )}

        {!enLigne && (
          <p className="capture-note">{page.sorte === 'journee' || page.sorte === 'agenda' ? MOTS.sansReseauPasDansLaJournee : MOTS.sansReseau}</p>
        )}
        {message && (
          <p className="capture-message" role="status">
            {message}
          </p>
        )}

        <button type="submit" className="bouton capture-ajouter" disabled={!parentId || !peutAjouter(sorte, titre, heure) || ajouter.isPending}>
          {MOTS.ajouter}
        </button>
      </form>

      {page.sorte === 'journee' && enLigne && (
        <div className="capture-possibles">
          <p className="capture-etiquette">{MOTS.ouParmiLesPossibles}</p>
          {possibles.data && possibles.data.possibles.length === 0 && <p className="capture-note">{MOTS.aucunPossible}</p>}
          <ul>
            {possibles.data?.possibles.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  className="capture-possible"
                  data-possible={p.id}
                  disabled={garder.isPending}
                  style={{ ['--teinte' as string]: teinteDuProjet(p.dossier?.couleur) }}
                  onClick={() => garderLaTache(p.id)}
                >
                  <span className="capture-point" aria-hidden="true" />
                  <span className="capture-possible-mots">
                    <b>{p.titre}</b>
                    <small>{[dureeLisible(p.minutes), p.dossier?.titre].filter(Boolean).join(' · ')}</small>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </FeuilleDuPlus>
  );
}
